import { BookingAction } from './booking-action';
import { BookingParams, BookingPosition, BookingResult } from '../types';

/**
 * Geteilter Kern aller Buchungen mit Quelle = Bufferstock. Hält die
 * welt-uniformen Richtungen:
 *   - Bufferstock → Bufferstock (NormalMovement, nur Phase 1; Phase 2 via UI)
 *   - Bufferstock → Maschine (OrderMovement, einphasig, Offline-Queue)
 *   - Bufferstock → Müll (TrashMovement, Offline-Queue)
 *   - Bufferstock → Verbrauch (ConsumptionMovement, Offline-Queue)
 *
 * Welt-eigene Richtungen leben in der jeweiligen Subklasse.
 */
export abstract class FromBufferstockActionBase extends BookingAction {
  /**
   * ONLINE-ONLY:
   * Bufferstock → Bufferstock: zweiphasige Umlagerung. Erzeugt ein
   * NormalMovement, das das Backend via TransferPoint in zwei Sub-Movements
   * zerlegt. Diese Action macht nur Phase 1 (Source → TransferPoint); Phase 2
   * (Empfangsbestätigung am Ziel) läuft über `bufferstock-load-by-booking`.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler, wenn Teil einer Transaction oder Remote-Speichern scheitert
   */
  protected async toBufferstock(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    if (context.booking.transactionId !== undefined) {
      throw Error(
        'Bufferstock->Bufferstock ist zweischrittig und kann daher nicht Teil einer Transaction sein',
      );
    }
    // 1. Create Movement remote
    const movement = adapters.movement.build(
      position,
      context.booking,
      params.clientIds.idFor(position),
    );
    const saved = await adapters.movement.createRemote(movement);
    if (!saved.value) {
      return this.failure(
        'Movement konnte nicht remote gespeichert werden',
        saved.retryable,
      );
    }
    return this.success();
  }

  /**
   * Bufferstock → Maschine: Standard-Befüll-Buchung. Erzeugt ein OrderMovement
   * (einphasig) in der Offline-Queue.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlender targetId, fehlendem Maschinenlager oder Speicher-Fehlschlag
   */
  protected async toMachine(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;
    if (booking.targetId === undefined) {
      return this.failure(
        'Bufferstock→Maschine: targetId fehlt. Maschine als Ziel erfordert eine ID',
      );
    }
    // Das Movement braucht die StockObject-ID des Maschinenlagers, nicht die
    // Maschinen-ID (booking.targetId) - sonst findet das Backend kein Stock und
    // der Bestand bleibt unverbucht (stiller Drift). Das lokale Maschinen-Update
    // unten läuft weiter über die Maschinen-ID (der MachineAdapter löst selbst auf).
    const machineId = booking.targetId;
    const stockObjectId =
      await adapters.machine.findStockObjectIdLocal(machineId);
    if (stockObjectId === null) {
      return this.failure(
        `Bufferstock→Maschine: Maschine ${machineId} hat kein Lager (StockObject)`,
      );
    }
    // 1. OrderMovement in die Offline-Queue (einphasig, kein bookRemote)
    const movement = adapters.movement.build(
      position,
      { ...booking, targetId: stockObjectId },
      params.clientIds.idFor(position),
    );
    const saved = await adapters.movement.enqueue(movement);
    if (!saved.value) {
      return this.failure(
        'OrderMovement konnte nicht in der Queue gespeichert werden',
        saved.retryable,
      );
    }
    // 2. Optionally enqueue booking for later rollback
    const rollbackFailure = this.enqueueForRollback(position, params);
    if (rollbackFailure) {
      return rollbackFailure;
    }
    // 3. Subtract from Bufferstock local
    const updatedBufferstock =
      await adapters.bufferstock.updatePositionAmountLocal(
        position,
        booking.sourceId,
        -position.amount,
      );
    if (!updatedBufferstock) {
      return this.failure(
        `Bufferstock ${booking.sourceId} konnte nicht gespeichert werden`,
      );
    }
    // 4. Add to Machine local
    const updatedMachine = await adapters.machine.updatePositionAmountLocal(
      position,
      booking.targetId,
      +position.amount,
    );
    if (!updatedMachine) {
      return this.failure(
        `Machine ${booking.targetId} konnte nicht gespeichert werden`,
      );
    }
    return this.success();
  }

  /**
   * Bufferstock → Müll: direkte Entsorgung vom Bufferstock. Erzeugt ein
   * TrashMovement in der Offline-Queue (einphasig). KEIN PositionDisposal: das
   * Backend legt für TrashMovement keine Row an, eine lokale Spiegelung würde
   * der Sync ohnehin überschreiben.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei Speicher- oder lokalem Bufferstock-Update-Fehlschlag
   */
  protected async toTrashBin(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;
    // 1. TrashMovement in die Offline-Queue
    const trashMovement = adapters.movement.build(
      position,
      booking,
      params.clientIds.idFor(position),
    );
    const savedMovement = await adapters.movement.enqueue(trashMovement);
    if (!savedMovement.value) {
      return this.failure(
        'TrashMovement konnte nicht gespeichert werden',
        savedMovement.retryable,
      );
    }
    // 2. Bufferstock lokal verringern. Optimistisch (Backend bucht beim
    //    Drainen ebenfalls, Down-Sync gleicht ab) UND nötig, damit der
    //    Overbook-Check bei Folgebuchungen den richtigen Bestand sieht.
    const bufferstockUpdated =
      await adapters.bufferstock.updatePositionAmountLocal(
        position,
        booking.sourceId,
        -position.amount,
      );
    if (!bufferstockUpdated) {
      return this.failure(
        `Bufferstock ${booking.sourceId} konnte nicht aktualisiert werden`,
      );
    }
    return this.success();
  }

  /**
   * Bufferstock → Verbrauch: Manueller Verbrauch (z.B. interne Nutzung).
   * Erzeugt ein ConsumptionMovement in der Offline-Queue (einphasig,
   * offline-fähig).
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei Speicher- oder lokalem Bufferstock-Update-Fehlschlag
   */
  protected async toConsumptionBin(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;
    // 1. ConsumptionMovement in die Offline-Queue
    const movement = adapters.movement.build(
      position,
      booking,
      params.clientIds.idFor(position),
    );
    const saved = await adapters.movement.enqueue(movement);
    if (!saved.value) {
      return this.failure(
        'ConsumptionMovement konnte nicht gespeichert werden',
        saved.retryable,
      );
    }
    // 2. Bufferstock lokal verringern (optimistisch + Overbook-Check,
    //    siehe toTrashBin)
    const bufferstockUpdated =
      await adapters.bufferstock.updatePositionAmountLocal(
        position,
        booking.sourceId,
        -position.amount,
      );
    if (!bufferstockUpdated) {
      return this.failure(
        `Bufferstock ${booking.sourceId} konnte nicht aktualisiert werden`,
      );
    }
    return this.success();
  }
}
